/**
 * Onglet Résultats Différé
 *
 * Navigation en 3 niveaux :
 * 1. Liste des tournois (sidebar) + résultats d'un tournoi (detail)
 * 2. Donnes d'un joueur dans un tournoi
 * 3. Replay d'une donne
 */

import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { PlayerAvatar } from '../components/PlayerAvatar';
import MasterDetail from '../components/MasterDetail';
import ReplayMode from '../components/ReplayMode';
import ReplayDualMode from '../components/ReplayDualMode';
import AddToLibraryModal from '../components/library/AddToLibraryModal';
import DiffereShareLinkModal from '../components/DiffereShareLinkModal';
import {
  MixteIcon, AttackIcon, AttackSurContratIcon, DefenseIcon,
  MaxiIcon, MaxiAttackIcon, MaxiDefenseIcon, NormalIcon,
  TripleteIcon, EquipeIcon,
} from '../components/Icons';
import { useApp } from '../contexts/AppContext';
import type { Session, DiffereNavigationRequest } from '../types/app';
import type { GameHistory } from '../game/gameHistory';
import { buildGameHistoryFromPartie, type ParsedPartie } from '../utils/differeReplay';
import {
  buildDiffereCompareReplayBatch,
  resolveDiffereBatchTitle,
} from '../utils/differeTrainingBatch';
import {
  buildLibraryDiffereOriginMetadata,
  fetchLibraryGameByOrigin,
} from '../utils/libraryOrigin';
import type { LibraryOriginMatch } from '../types/library';
import '../styles/differe-results.css';
import { BACKEND_URL } from '../config';
import { Switch } from '../components/Switch';

type DiffereType = 'indiv' | 'tripl' | 'quad';

// Detail navigation
type DetailView = 'results' | 'donnes' | 'replay';

interface DiffereListItem {
  iddiff: number;
  pseudo?: string;
  pseudo_display?: string;
  type?: string;
  debut?: string;
  fin: string;
  nb_participants?: number;
  player_rank?: number;
  player_tot?: number;
  top_pseudo?: string;
  top_pseudo_display?: string;
  top_count?: number;
  // tripl
  idtriplette?: number;
  joueurs?: string[];
  joueurs_display?: string[];
  // quad
  equipe_id?: number;
  tot?: number;
}

interface TopPopoverState {
  diffId: number;
  rect: DOMRect;
  pseudos: string[];
  displays: string[];
}

interface DiffereInfo {
  id: number;
  debut?: string;
  fin: string;
  type?: string;
  donnes?: string;
  donnes_att_idx?: string;
}

interface ResultItem {
  pseudo?: string;
  pseudo_display?: string;
  idtriplette?: number;
  idequipe?: number;
  equipe_nom?: string;
  ouest?: string;
  nord?: string;
  est?: string;
  ouest_display?: string;
  nord_display?: string;
  est_display?: string;
  tot: number;
  att: number;
  def: number;
  [key: string]: unknown;
}

interface DonneItem {
  id: number;
  idtournoi: number;
  numdonne: number;
  [key: string]: unknown;
}

interface ScoresData {
  tot?: number;
  att?: number;
  def?: number;
  [key: string]: unknown;
}

// ============================================
// Helpers
// ============================================

function formatDate(dateStr: string): string {
  try {
    const d = new Date(dateStr);
    return d.toLocaleDateString('fr-FR', { day: '2-digit', month: '2-digit', year: '2-digit' });
  } catch {
    return dateStr;
  }
}

function getScoreKey(donneIndex: number, prefix: string): string {
  return `${prefix}${donneIndex + 1}`;
}

function getPercentage(score: number, effectif: number): number {
  if (effectif <= 1) return 0;
  return (score / ((effectif - 1) * 2)) * 100;
}

function getRankInList(playerScore: number, allScores: number[]): number {
  return allScores.filter(s => s > playerScore).length + 1;
}

function typeLabel(type: DiffereType): string {
  switch (type) {
    case 'indiv': return 'Individuel';
    case 'tripl': return 'Triplette';
    case 'quad': return 'Équipe';
  }
}

/**
 * Identité d'une ligne de résultat : `playerParam` est ce qu'attend l'endpoint
 * donnes (un pseudo en individuel et en triplette, l'id d'équipe en quadrette).
 */
function resultPlayerIdentity(
  item: ResultItem,
  diffType: DiffereType,
  idx: number,
): { playerParam: string; display: string; key: string } {
  if (diffType === 'indiv') {
    return {
      playerParam: item.pseudo || '',
      display: item.pseudo_display || item.pseudo || '',
      key: item.pseudo || `i-${idx}`,
    };
  }
  if (diffType === 'tripl') {
    const members = [
      { pseudo: item.ouest || '', display: item.ouest_display || item.ouest || '' },
      { pseudo: item.nord || '', display: item.nord_display || item.nord || '' },
      { pseudo: item.est || '', display: item.est_display || item.est || '' },
    ];
    return {
      playerParam: members[0].pseudo,
      display: members.map((m) => m.display).join(' / '),
      key: String(item.idtriplette ?? `t-${idx}`),
    };
  }
  return {
    playerParam: String(item.idequipe ?? ''),
    display: item.equipe_nom || `Équipe ${item.idequipe}`,
    key: String(item.idequipe ?? `q-${idx}`),
  };
}

/**
 * Recentre l'historique sur un joueur : buildGameHistoryFromPartie place
 * toujours le siège 0 en bas, ce qui n'est pertinent qu'en vue simple. En
 * comparaison, chaque volet doit être vu depuis son propre joueur.
 */
function historyWithViewer(history: GameHistory, viewerPseudo?: string | null): GameHistory {
  if (!viewerPseudo) return history;
  const norm = (v: string) => v.toLowerCase().replace(/¶/g, ' ').trim();
  const target = norm(viewerPseudo);
  const seat = history.players.find((p) => norm(p.name) === target);
  if (!seat || seat.position === history.myPosition) return history;
  return { ...history, myPosition: seat.position };
}

const TYPE_LABELS: Record<string, string> = {
  mixte: 'Mixte',
  normal: 'Normal',
  maxi: 'Maxi',
  attaque: 'Attaque',
  defense: 'Défense',
  attaqueSC: 'Attaque SC',
  maxiAtt: 'Maxi Attaque',
  maxiDef: 'Maxi Défense',
};

const SUB_TYPE_COLORS: Record<string, string> = {
  mixte: '#0d9488',
  normal: '#6b7280',
  maxi: '#f59e0b',
  attaque: '#ef4444',
  defense: '#3b82f6',
  attaqueSC: '#dc2626',
  maxiAtt: '#f97316',
  maxiDef: '#8b5cf6',
};

type SubFilter = 'all' | 'att' | 'def';
type SortKey = 'tot' | 'att' | 'def';
type ViewMode = 'byPlayer' | 'byDonne';

const ATT_TYPES = new Set(['attaque', 'attaqueSC', 'maxiAtt']);
const DEF_TYPES = new Set(['defense', 'maxiDef']);
// indiv differes that have both attack & defense donnes (need ATT/DEF columns)
const MIXED_INDIV_TYPES = new Set(['mixte', 'maxi']);

function parseAttIdxSet(raw?: string): Set<number> {
  if (!raw) return new Set();
  const s = new Set<number>();
  for (const part of raw.split(',')) {
    const n = parseInt(part, 10);
    if (Number.isFinite(n)) s.add(n);
  }
  return s;
}

function isAttDonne(donne0Idx: number, attSet: Set<number>): boolean {
  return attSet.has(donne0Idx);
}

/**
 * Whether ATT/DEF breakdown is meaningful for this differe
 *  - tripl/quad : always
 *  - indiv : only mixte/maxi (others are mono-type)
 */
function hasAttDefBreakdown(
  diffType: DiffereType,
  legacyType: string | undefined,
): boolean {
  if (diffType !== 'indiv') return true;
  return MIXED_INDIV_TYPES.has(legacyType || '');
}

/**
 * For a mono-type indiv differe, returns whether the (single) total
 * represents attack ('att'), defense ('def') or unknown (null).
 */
function indivMonoCategory(legacyType: string | undefined): 'att' | 'def' | null {
  if (!legacyType) return null;
  if (ATT_TYPES.has(legacyType)) return 'att';
  if (DEF_TYPES.has(legacyType)) return 'def';
  return null;
}

function getRankQuality(rank: number, total: number): 'excellent' | 'good' | 'average' | 'poor' {
  if (total <= 0) return 'average';
  if (rank <= 3) return 'excellent';
  const pct = rank / total;
  if (pct <= 0.25) return 'good';
  if (pct <= 0.60) return 'average';
  return 'poor';
}

interface PlayerSearchResult {
  pseudo: string;
  pseudoDisplay: string;
  photoUrl?: string | null;
}

function getSubTypeIcon(typeCode: string | undefined, size = 16): React.ReactNode | null {
  switch (typeCode) {
    case 'maxiDef': return <MaxiDefenseIcon size={size} />;
    case 'maxiAtt': return <MaxiAttackIcon size={size} />;
    case 'maxi': return <MaxiIcon size={size} />;
    case 'attaqueSC': return <AttackSurContratIcon size={size} />;
    case 'attaque': return <AttackIcon size={size} />;
    case 'defense': return <DefenseIcon size={size} />;
    case 'mixte': return <MixteIcon size={size} />;
    case 'normal': return <NormalIcon size={size} />;
    default: return null;
  }
}

function getCategoryIcon(diffType: DiffereType, size = 16): React.ReactNode {
  switch (diffType) {
    case 'tripl': return <TripleteIcon size={size} />;
    case 'quad': return <EquipeIcon size={size} />;
    default: return null;
  }
}

function isTournamentOngoing(fin: string): boolean {
  try {
    const finDate = new Date(fin);
    const today = new Date();
    finDate.setHours(0, 0, 0, 0);
    today.setHours(0, 0, 0, 0);
    return finDate >= today;
  } catch {
    return false;
  }
}

function formatDateRange(debut?: string, fin?: string): string {
  const finStr = fin ? formatDate(fin) : '';
  if (!debut) return finStr;
  const debutStr = formatDate(debut);
  if (debutStr === finStr) return finStr;
  return `${debutStr} → ${finStr}`;
}

// ============================================
// Component
// ============================================

interface DiffereResultsProps {
  session: Session;
}

export default function DiffereResults({ session }: DiffereResultsProps) {
  const {
    startTraining,
    preferences,
    updatePreferences,
    differeNavigationRequest,
    clearDiffereNavigationRequest,
    navigateToLibrary,
  } = useApp();

  const skipDetailResetRef = useRef(false);
  const skipTournamentResetRef = useRef(false);
  const skipSelectedDiffFetchRef = useRef(false);

  // --- State: type selector ---
  const [diffType, setDiffType] = useState<DiffereType>('indiv');

  // --- State: tournament list ---
  const [tournaments, setTournaments] = useState<DiffereListItem[]>([]);
  const [tournamentsLoading, setTournamentsLoading] = useState(false);
  const [tournamentsError, setTournamentsError] = useState<string | null>(null);
  const [selectedDiffId, setSelectedDiffId] = useState<number | null>(null);
  const [showMobileDetail, setShowMobileDetail] = useState(false);
  const [onlyMine, setOnlyMine] = useState(preferences.differeResultsOnlyMine);
  const [subFilter, setSubFilter] = useState<SubFilter>('all');

  // --- State: followed player (when onlyMine unchecked) ---
  const [followedPlayer, setFollowedPlayer] = useState<{ pseudo: string; display: string } | null>(null);
  const [playerSearchQuery, setPlayerSearchQuery] = useState('');
  const [playerSearchResults, setPlayerSearchResults] = useState<PlayerSearchResult[]>([]);
  const [playerSearchLoading, setPlayerSearchLoading] = useState(false);
  const [showPlayerDropdown, setShowPlayerDropdown] = useState(false);
  const playerSearchRef = useRef<HTMLDivElement>(null);
  const playerSearchTimeout = useRef<ReturnType<typeof setTimeout> | null>(null);

  // --- State: results ---
  const [differeInfo, setDiffereInfo] = useState<DiffereInfo | null>(null);
  const [results, setResults] = useState<ResultItem[]>([]);
  const [resultsLoading, setResultsLoading] = useState(false);
  const [resultsError, setResultsError] = useState<string | null>(null);

  // --- State: donnes ---
  const [detailView, setDetailView] = useState<DetailView>('results');
  const [selectedPlayerPseudo, setSelectedPlayerPseudo] = useState<string | null>(null);
  const [selectedPlayerDisplay, setSelectedPlayerDisplay] = useState<string>('');
  const [donnes, setDonnes] = useState<DonneItem[]>([]);
  const [donneScores, setDonneScores] = useState<ScoresData | null>(null);
  const [donnesLoading, setDonnesLoading] = useState(false);
  const [donnesError, setDonnesError] = useState<string | null>(null);

  // --- State: replay ---
  const [replayHistory, setReplayHistory] = useState<GameHistory | null>(null);
  const [replayLoading, setReplayLoading] = useState(false);
  const [replayDonneNum, setReplayDonneNum] = useState<number | null>(null);

  // --- State: comparaison double tables ---
  const [dualMode, setDualMode] = useState(false);
  const [comparePlayer, setComparePlayer] = useState<{ param: string; display: string } | null>(null);
  const [compareHistory, setCompareHistory] = useState<GameHistory | null>(null);
  const [compareLoading, setCompareLoading] = useState(false);
  const [compareError, setCompareError] = useState<string | null>(null);
  /** Donnes déjà récupérées par joueur comparé, pour ne pas refetcher à chaque donne. */
  const compareDonnesCache = useRef<Map<string, DonneItem[]>>(new Map());
  // --- State: library modal ---
  const [showLibraryModal, setShowLibraryModal] = useState(false);
  const [trainingExportLoading, setTrainingExportLoading] = useState(false);
  const [libraryMatch, setLibraryMatch] = useState<LibraryOriginMatch | null>(null);
  const [libraryMatchLoading, setLibraryMatchLoading] = useState(false);
  const [showShareLinkModal, setShowShareLinkModal] = useState(false);

  // --- State: top player popover ---
  const [topPopover, setTopPopover] = useState<TopPopoverState | null>(null);

  // --- State: results view mode + sort ---
  const [viewMode, setViewMode] = useState<ViewMode>('byPlayer');
  const [sortKey, setSortKey] = useState<SortKey>('tot');
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>('desc');

  // --- State: donne picker (in replay banner) ---
  const [showDonnePicker, setShowDonnePicker] = useState(false);

  const abortRef = useRef<AbortController | null>(null);
  const resultsBodyRef = useRef<HTMLDivElement>(null);
  const donnePickerRef = useRef<HTMLDivElement>(null);

  // ==========================================
  // Fetch tournament list
  // ==========================================
  const effectivePlayer = onlyMine ? session.pseudo : followedPlayer?.pseudo || null;

  const fetchTournaments = useCallback(async () => {
    if (abortRef.current) abortRef.current.abort();
    const controller = new AbortController();
    abortRef.current = controller;

    setTournamentsLoading(true);
    setTournamentsError(null);

    try {
      const params = new URLSearchParams({ days: '365', limit: '200' });
      if (effectivePlayer) {
        params.set('player', effectivePlayer);
      }
      const res = await fetch(
        `${BACKEND_URL}/api/competition/differe/${diffType}/list?${params}`,
        { signal: controller.signal },
      );
      if (!res.ok) throw new Error(`Erreur ${res.status}`);
      const json = await res.json();
      if (!json.success) throw new Error(json.error || 'Erreur inconnue');
      setTournaments(json.data?.items ?? []);
      setTournamentsLoading(false);
    } catch (e) {
      if (e instanceof Error && e.name === 'AbortError') return;
      setTournamentsError(e instanceof Error ? e.message : 'Erreur réseau');
      setTournamentsLoading(false);
    }
  }, [diffType, effectivePlayer]);

  useEffect(() => {
    if (!skipTournamentResetRef.current) {
      setSelectedDiffId(null);
      setResults([]);
      setDetailView('results');
      setReplayHistory(null);
    }
    skipTournamentResetRef.current = false;
    fetchTournaments();
  }, [fetchTournaments]);

  // ==========================================
  // Fetch results for a tournament
  // ==========================================
  const fetchResults = useCallback(async (diffId: number, typeOverride?: DiffereType) => {
    const activeType = typeOverride ?? diffType;
    setResultsLoading(true);
    setResultsError(null);
    setResults([]);
    setDiffereInfo(null);

    try {
      const res = await fetch(
        `${BACKEND_URL}/api/competition/differe/${activeType}/${diffId}/results`,
      );
      if (!res.ok) throw new Error(`Erreur ${res.status}`);
      const json = await res.json();
      if (!json.success) throw new Error(json.error || 'Erreur inconnue');
      setDiffereInfo(json.data?.differe ?? null);
      setResults(json.data?.items ?? []);
    } catch (e) {
      setResultsError(e instanceof Error ? e.message : 'Erreur réseau');
    } finally {
      setResultsLoading(false);
    }
  }, [diffType]);

  useEffect(() => {
    if (selectedDiffId !== null) {
      if (!skipDetailResetRef.current) {
        setDetailView('results');
        setReplayHistory(null);
        setViewMode('byPlayer');
        setSortKey('tot');
        setSortDir('desc');
      }
      skipDetailResetRef.current = false;
      if (!skipSelectedDiffFetchRef.current) {
        fetchResults(selectedDiffId);
      }
      skipSelectedDiffFetchRef.current = false;
    }
  }, [selectedDiffId, fetchResults]);

  // Auto-scroll to tracked player when results load
  useEffect(() => {
    if (detailView !== 'results' || results.length === 0 || !effectivePlayer) return;
    const timer = setTimeout(() => {
      const el = resultsBodyRef.current?.querySelector('.differe-results-row.is-tracked');
      if (el) {
        el.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }
    }, 120);
    return () => clearTimeout(timer);
  }, [results, detailView, effectivePlayer]);

  // ==========================================
  // Fetch donnes for a player in a tournament
  // ==========================================
  const fetchDonnes = useCallback(async (
    diffId: number,
    player: string,
    typeOverride?: DiffereType,
  ): Promise<{ donnes: DonneItem[]; scores: ScoresData | null } | null> => {
    const activeType = typeOverride ?? diffType;
    setDonnesLoading(true);
    setDonnesError(null);
    setDonnes([]);
    setDonneScores(null);

    try {
      const params = new URLSearchParams({ player });
      const res = await fetch(
        `${BACKEND_URL}/api/competition/differe/${activeType}/${diffId}/donnes?${params}`,
      );
      const json = await res.json();
      if (!json.success) throw new Error(json.error || `Erreur ${res.status}`);
      const donnesList: DonneItem[] = json.data?.donnes ?? [];
      const scores: ScoresData | null = json.data?.scores ?? null;
      setDonnes(donnesList);
      setDonneScores(scores);
      return { donnes: donnesList, scores };
    } catch (e) {
      setDonnesError(e instanceof Error ? e.message : 'Erreur réseau');
      return null;
    } finally {
      setDonnesLoading(false);
    }
  }, [diffType]);

  const handlePlayerClick = useCallback((player: string, displayName: string) => {
    if (selectedDiffId === null) return;
    setSelectedPlayerPseudo(player);
    setSelectedPlayerDisplay(displayName);
    setDetailView('donnes');
    fetchDonnes(selectedDiffId, player);
  }, [selectedDiffId, fetchDonnes]);

  // ==========================================
  // Fetch replay for a donne
  // ==========================================
  const fetchReplay = useCallback(async (
    partieId: number,
    donneNum: number,
    typeOverride?: DiffereType,
  ) => {
    const activeType = typeOverride ?? diffType;
    setReplayLoading(true);
    setReplayDonneNum(donneNum);

    try {
      const res = await fetch(
        `${BACKEND_URL}/api/competition/differe/${activeType}/replay/${partieId}`,
      );
      if (!res.ok) throw new Error(`Erreur ${res.status}`);
      const json = await res.json();
      if (!json.success) throw new Error(json.error || 'Erreur inconnue');

      const partie = json.data?.partie as ParsedPartie;
      if (partie) {
        const history = buildGameHistoryFromPartie(partie);
        setReplayHistory(history);
        setDetailView('replay');
      }
    } catch (e) {
      const msg = e instanceof Error ? e.message : 'Erreur réseau';
      console.error('Replay fetch error:', msg);
    } finally {
      setReplayLoading(false);
    }
  }, [diffType]);

  const applyDiffereNavigation = useCallback(async (req: DiffereNavigationRequest) => {
    skipTournamentResetRef.current = true;
    skipDetailResetRef.current = true;
    skipSelectedDiffFetchRef.current = true;
    setShowMobileDetail(true);
    setSubFilter('all');

    if (req.diffType) setDiffType(req.diffType);
    setSelectedDiffId(req.differeId);

    if (req.playerPseudo) {
      setSelectedPlayerPseudo(req.playerPseudo);
      setSelectedPlayerDisplay(req.playerDisplay ?? req.playerPseudo);
    }

    const activeType = req.diffType;

    try {
      await fetchResults(req.differeId, activeType);

      let partieId = req.partieId;
      if (req.playerPseudo) {
        const donnesData = await fetchDonnes(req.differeId, req.playerPseudo, activeType);
        if (!partieId && req.dealNum != null && donnesData) {
          const found = donnesData.donnes.find((d) => Number(d.numdonne) === req.dealNum);
          partieId = found ? Number(found.id) : undefined;
        }
      }

      if (req.view === 'replay' && partieId != null && req.dealNum != null) {
        await fetchReplay(partieId, req.dealNum, activeType);
      } else if (req.view === 'donnes' && req.playerPseudo) {
        setDetailView('donnes');
      }
    } catch (e) {
      console.error('Différé navigation failed:', e);
    }
  }, [fetchResults, fetchDonnes, fetchReplay]);

  // Navigation profonde depuis l'historique entraînement
  useEffect(() => {
    if (!differeNavigationRequest) return;
    const req = differeNavigationRequest;
    clearDiffereNavigationRequest();
    void applyDiffereNavigation(req);
  }, [differeNavigationRequest, clearDiffereNavigationRequest, applyDiffereNavigation]);

  // ==========================================
  // Score columns for donnes
  // ==========================================
  const donneCount = useMemo(() => {
    if (!differeInfo?.donnes) return 0;
    return differeInfo.donnes.split(',').length;
  }, [differeInfo]);

  const effectif = results.length;

  // Set of 0-based donne indexes that are attack donnes
  const attDonneSet = useMemo(
    () => parseAttIdxSet(differeInfo?.donnes_att_idx),
    [differeInfo],
  );

  // Whether to show the ATT/DEF columns (vs only TOT)
  const showAttDef = useMemo(
    () => hasAttDefBreakdown(diffType, differeInfo?.type),
    [diffType, differeInfo],
  );

  // If sort key becomes invalid (e.g. att/def hidden), fallback to tot
  useEffect(() => {
    if (!showAttDef && (sortKey === 'att' || sortKey === 'def')) {
      setSortKey('tot');
    }
  }, [showAttDef, sortKey]);

  // For mono-type indiv differes, the "tot" represents this category
  const monoCategory = useMemo(
    () => (diffType === 'indiv' ? indivMonoCategory(differeInfo?.type) : null),
    [diffType, differeInfo],
  );

  // For each donne, the array of (raw) s-scores from all results, with null
  // for missing values (used both for ranking and effectif computation).
  const donnePerScores = useMemo(() => {
    const map: Array<Array<number | null>> = [];
    for (let i = 0; i < donneCount; i++) {
      const key = `s${i + 1}`;
      map.push(results.map(r => {
        const v = r[key];
        if (v == null || v === '') return null;
        const n = Number(v);
        return Number.isFinite(n) ? n : null;
      }));
    }
    return map;
  }, [results, donneCount]);

  // Number-only flat list used for ranking display (legacy compatible)
  const donneAllScores = useMemo(() => {
    const map: Record<number, number[]> = {};
    for (let i = 0; i < donneCount; i++) {
      map[i] = donnePerScores[i]?.map(s => s ?? 0) ?? [];
    }
    return map;
  }, [donnePerScores, donneCount]);

  // Effectif per donne (count of valid scores)
  const effectifPerDonne = useMemo(
    () => donnePerScores.map(scores => scores.filter(s => s !== null).length),
    [donnePerScores],
  );

  // Max-possible totals (sur) for tot/att/def — sum of (effectif_i - 1) * 2
  const surTotals = useMemo(() => {
    let tot = 0, att = 0, def = 0;
    for (let i = 0; i < effectifPerDonne.length; i++) {
      const eff = effectifPerDonne[i];
      const s = eff > 1 ? (eff - 1) * 2 : 0;
      tot += s;
      if (showAttDef) {
        if (isAttDonne(i, attDonneSet)) att += s;
        else def += s;
      }
    }
    return { tot, att, def };
  }, [effectifPerDonne, showAttDef, attDonneSet]);

  // ==========================================
  // Donne navigation (when a player is selected)
  // ==========================================
  const playerDonneList = useMemo(() => {
    if (!donneScores) return [] as Array<{
      donne: DonneItem;
      idx: number;
      score: number | null;
      points: number | null;
      pct: number;
      rank: number;
      isAtt: boolean;
    }>;
    return donnes.map((donne) => {
      const idx = Number(donne.numdonne) - 1;
      const sKey = `s${idx + 1}`;
      const pKey = `p${idx + 1}`;
      const sRaw = (donneScores as Record<string, unknown>)[sKey];
      const pRaw = (donneScores as Record<string, unknown>)[pKey];
      const score = sRaw == null || sRaw === '' ? null : Number(sRaw);
      const points = pRaw == null || pRaw === '' ? null : Number(pRaw);
      const eff = effectifPerDonne[idx] ?? effectif;
      const sur = eff > 1 ? (eff - 1) * 2 : 0;
      const pct = points != null && sur > 0 ? (points / sur) * 100 : 0;
      const allScores = donneAllScores[idx] || [];
      const rank = score != null ? getRankInList(score, allScores) : 0;
      return {
        donne,
        idx,
        score,
        points,
        pct,
        rank,
        isAtt: isAttDonne(idx, attDonneSet),
      };
    });
  }, [donnes, donneScores, effectifPerDonne, effectif, donneAllScores, attDonneSet]);

  // Index of the currently replayed donne within playerDonneList
  const currentDonneIdx = useMemo(() => {
    if (replayDonneNum == null) return -1;
    return playerDonneList.findIndex(d => Number(d.donne.numdonne) === replayDonneNum);
  }, [playerDonneList, replayDonneNum]);

  const currentPartieId = useMemo(() => {
    if (currentDonneIdx >= 0) return Number(playerDonneList[currentDonneIdx]?.donne.id);
    if (replayDonneNum != null) {
      const found = donnes.find((d) => Number(d.numdonne) === replayDonneNum);
      return found ? Number(found.id) : undefined;
    }
    return undefined;
  }, [currentDonneIdx, playerDonneList, replayDonneNum, donnes]);

  // ==========================================
  // Comparaison double tables
  // ==========================================

  const replayViewerParam = selectedPlayerPseudo ?? effectivePlayer ?? session.pseudo;

  /** Table de gauche recentrée sur le joueur consulté (identité stable). */
  const dualLeftHistory = useMemo(
    () => (replayHistory ? historyWithViewer(replayHistory, replayViewerParam) : null),
    [replayHistory, replayViewerParam],
  );

  /** Participants ayant joué la donne affichée, du meilleur score au moins bon. */
  const compareCandidates = useMemo(() => {
    if (replayDonneNum == null) return [];
    const scores = donnePerScores[replayDonneNum - 1] || [];
    const viewed = (selectedPlayerPseudo ?? effectivePlayer ?? '').toLowerCase();
    return results
      .map((item, idx) => {
        const identity = resultPlayerIdentity(item, diffType, idx);
        return { ...identity, score: scores[idx] ?? null };
      })
      .filter((c) => c.playerParam && c.score !== null)
      .filter((c) => c.playerParam.toLowerCase() !== viewed)
      .sort((a, b) => (b.score ?? 0) - (a.score ?? 0));
  }, [results, donnePerScores, replayDonneNum, diffType, selectedPlayerPseudo, effectivePlayer]);

  /** Charge la table du joueur comparé pour la donne affichée. */
  const loadCompareHistory = useCallback(async (playerParam: string) => {
    if (selectedDiffId === null || replayDonneNum == null || !playerParam) return;
    setCompareLoading(true);
    setCompareError(null);
    try {
      let list = compareDonnesCache.current.get(playerParam);
      if (!list) {
        const res = await fetch(
          `${BACKEND_URL}/api/competition/differe/${diffType}/${selectedDiffId}/donnes?player=${encodeURIComponent(playerParam)}`,
        );
        const json = await res.json();
        if (!json.success) throw new Error(json.error || 'Erreur inconnue');
        list = (json.data?.donnes ?? []) as DonneItem[];
        compareDonnesCache.current.set(playerParam, list);
      }

      const entry = list.find((d) => Number(d.numdonne) === replayDonneNum);
      if (!entry) throw new Error("Ce joueur n'a pas joué cette donne");

      const res = await fetch(`${BACKEND_URL}/api/competition/differe/replay/${entry.id}`);
      const json = await res.json();
      if (!json.success) throw new Error(json.error || 'Erreur inconnue');
      const partie = json.data?.partie as ParsedPartie;
      if (!partie) throw new Error('Donne indisponible');
      setCompareHistory(historyWithViewer(buildGameHistoryFromPartie(partie), playerParam));
    } catch (e) {
      setCompareHistory(null);
      setCompareError(e instanceof Error ? e.message : 'Erreur réseau');
    } finally {
      setCompareLoading(false);
    }
  }, [selectedDiffId, replayDonneNum, diffType]);

  // Joueur comparé par défaut : le top de la donne affichée
  useEffect(() => {
    if (!dualMode || detailView !== 'replay') return;
    const stillPresent =
      comparePlayer && compareCandidates.some((c) => c.playerParam === comparePlayer.param);
    if (stillPresent) return;
    const top = compareCandidates[0];
    setComparePlayer(top ? { param: top.playerParam, display: top.display } : null);
  }, [dualMode, detailView, compareCandidates, comparePlayer]);

  // (Re)charge la table de droite à chaque changement de joueur comparé ou de donne
  useEffect(() => {
    if (!dualMode || detailView !== 'replay' || !comparePlayer) {
      return;
    }
    void loadCompareHistory(comparePlayer.param);
  }, [dualMode, detailView, comparePlayer, loadCompareHistory]);

  // Le cache de donnes est propre à un différé
  useEffect(() => {
    compareDonnesCache.current.clear();
    setComparePlayer(null);
    setCompareHistory(null);
  }, [selectedDiffId, diffType]);

  const refreshLibraryMatch = useCallback(async () => {
    if (detailView !== 'replay' || selectedDiffId === null || replayDonneNum == null) {
      setLibraryMatch(null);
      return;
    }
    setLibraryMatchLoading(true);
    try {
      const playerPseudo = selectedPlayerPseudo ?? effectivePlayer ?? session.pseudo;
      const result = await fetchLibraryGameByOrigin({
        pseudo: session.pseudo,
        originType: diffType === 'indiv' ? 'differe_indiv' : 'differe_tq',
        originId: String(selectedDiffId),
        originDonneNum: replayDonneNum,
        partieId: currentPartieId,
        playerPseudo,
      });
      setLibraryMatch(result.found ? result.game : null);
    } catch {
      setLibraryMatch(null);
    } finally {
      setLibraryMatchLoading(false);
    }
  }, [
    detailView,
    selectedDiffId,
    replayDonneNum,
    diffType,
    session.pseudo,
    selectedPlayerPseudo,
    effectivePlayer,
    currentPartieId,
  ]);

  useEffect(() => {
    void refreshLibraryMatch();
  }, [refreshLibraryMatch]);

  const currentDiffereShareRequest = useMemo((): DiffereNavigationRequest | null => {
    if (selectedDiffId === null || replayDonneNum == null) return null;
    const playerPseudo = selectedPlayerPseudo ?? effectivePlayer ?? session.pseudo;
    if (!playerPseudo) return null;
    return {
      diffType,
      differeId: selectedDiffId,
      playerPseudo,
      playerDisplay: selectedPlayerDisplay || followedPlayer?.display,
      dealNum: replayDonneNum,
      partieId: currentPartieId,
      view: 'replay',
    };
  }, [
    selectedDiffId,
    replayDonneNum,
    diffType,
    selectedPlayerPseudo,
    effectivePlayer,
    session.pseudo,
    selectedPlayerDisplay,
    followedPlayer,
    currentPartieId,
  ]);

  // ==========================================
  // Tournament selector (sidebar)
  // ==========================================
  const handleTournamentClick = (diffId: number) => {
    setSelectedDiffId(diffId);
    setShowMobileDetail(true);
  };

  // Sub-type client-side filtering (indiv only)
  const filteredTournaments = useMemo(() => {
    if (diffType !== 'indiv' || subFilter === 'all') return tournaments;
    if (subFilter === 'att') return tournaments.filter(t => ATT_TYPES.has(t.type || ''));
    if (subFilter === 'def') return tournaments.filter(t => DEF_TYPES.has(t.type || ''));
    return tournaments;
  }, [tournaments, diffType, subFilter]);

  // Player search (autocomplete)
  const searchPlayers = useCallback(async (q: string) => {
    if (q.length < 2) { setPlayerSearchResults([]); return; }
    setPlayerSearchLoading(true);
    try {
      const res = await fetch(`${BACKEND_URL}/api/players/search?q=${encodeURIComponent(q)}&limit=10&compet=true`);
      const json = await res.json();
      setPlayerSearchResults(json.players || []);
    } catch {
      setPlayerSearchResults([]);
    } finally {
      setPlayerSearchLoading(false);
    }
  }, []);

  const handlePlayerSearchInput = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const val = e.target.value;
    setPlayerSearchQuery(val);
    setShowPlayerDropdown(true);
    if (playerSearchTimeout.current) clearTimeout(playerSearchTimeout.current);
    playerSearchTimeout.current = setTimeout(() => searchPlayers(val), 250);
  }, [searchPlayers]);

  const handleSelectFollowedPlayer = useCallback((pseudo: string, display: string) => {
    setFollowedPlayer({ pseudo, display });
    setPlayerSearchQuery('');
    setPlayerSearchResults([]);
    setShowPlayerDropdown(false);
  }, []);

  const handleClearFollowedPlayer = useCallback(() => {
    setFollowedPlayer(null);
    setPlayerSearchQuery('');
    setPlayerSearchResults([]);
  }, []);

  // Close dropdown / top popover on outside click
  useEffect(() => {
    function handleClickOutside(e: MouseEvent) {
      if (playerSearchRef.current && !playerSearchRef.current.contains(e.target as Node)) {
        setShowPlayerDropdown(false);
      }
      const target = e.target as HTMLElement;
      if (topPopover && !target.closest('.differe-top-popover') && !target.closest('.differe-top-avatar-wrap')) {
        setTopPopover(null);
      }
      if (
        showDonnePicker &&
        donnePickerRef.current &&
        !donnePickerRef.current.contains(target) &&
        !target.closest('.differe-donne-picker-trigger')
      ) {
        setShowDonnePicker(false);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [topPopover, showDonnePicker]);

  // Reset followed player when toggling onlyMine
  useEffect(() => {
    if (onlyMine) {
      setFollowedPlayer(null);
      setPlayerSearchQuery('');
      setPlayerSearchResults([]);
    }
  }, [onlyMine]);

  const sidebarContent = (
    <>
      <div className="differe-sidebar-header">
        <h2 className="differe-sidebar-title">Résultats Différé</h2>
        <div className="differe-type-selector">
          {(['indiv', 'tripl', 'quad'] as DiffereType[]).map((t) => (
            <button
              key={t}
              className={`differe-type-btn ${diffType === t ? 'active' : ''}`}
              onClick={() => { setDiffType(t); setSubFilter('all'); }}
            >
              {t === 'indiv' ? '👤' : getCategoryIcon(t, 18)}
              <span>{typeLabel(t)}</span>
            </button>
          ))}
        </div>

        {/* Sub-type filter (indiv only) */}
        {diffType === 'indiv' && (
          <div className="differe-sub-filter">
            {([['all', 'Tout'], ['att', 'Attaque'], ['def', 'Défense']] as const).map(([key, label]) => (
              <button
                key={key}
                className={`differe-sub-filter-btn ${subFilter === key ? 'active' : ''}`}
                onClick={() => setSubFilter(key as SubFilter)}
              >
                {key === 'att' && <AttackIcon size={12} />}
                {key === 'def' && <DefenseIcon size={12} />}
                {label}
              </button>
            ))}
          </div>
        )}

        {/* Hide checkbox when a followed player is selected */}
        {!followedPlayer && (
          <label className="differe-filter-mine">
            Mes tournois uniquement
            <Switch
              compact
              checked={onlyMine}
              onChange={(v) => {
                setOnlyMine(v);
                updatePreferences({ differeResultsOnlyMine: v });
              }}
            />
          </label>
        )}

        {/* Autocomplete player search (when onlyMine is unchecked) */}
        {!onlyMine && (
          <div className="differe-player-search" ref={playerSearchRef}>
            {followedPlayer ? (
              <div className="differe-followed-chip">
                <PlayerAvatar pseudo={followedPlayer.pseudo} size={22} />
                <span className="differe-followed-name">{followedPlayer.display}</span>
                <button
                  className="differe-followed-clear"
                  onClick={handleClearFollowedPlayer}
                  title="Retirer le filtre joueur"
                >✕</button>
              </div>
            ) : (
              <div className="differe-search-input-wrap">
                <input
                  type="text"
                  className="differe-search-input"
                  placeholder="Rechercher un joueur..."
                  value={playerSearchQuery}
                  onChange={handlePlayerSearchInput}
                  onFocus={() => { if (playerSearchQuery.length >= 2) setShowPlayerDropdown(true); }}
                />
                {playerSearchLoading && <div className="differe-search-spinner" />}
              </div>
            )}
            {showPlayerDropdown && playerSearchResults.length > 0 && !followedPlayer && (
              <div className="differe-search-dropdown">
                {playerSearchResults.map(p => (
                  <button
                    key={p.pseudo}
                    className="differe-search-option"
                    onClick={() => handleSelectFollowedPlayer(p.pseudo, p.pseudoDisplay)}
                  >
                    <PlayerAvatar pseudo={p.pseudo} size={24} photoUrl={p.photoUrl} />
                    <span>{p.pseudoDisplay}</span>
                  </button>
                ))}
              </div>
            )}
            {showPlayerDropdown && playerSearchQuery.length >= 2 && !playerSearchLoading && playerSearchResults.length === 0 && !followedPlayer && (
              <div className="differe-search-dropdown">
                <div className="differe-search-empty">Aucun joueur trouvé</div>
              </div>
            )}
          </div>
        )}
      </div>

      <div className="differe-tournament-list">
        {tournamentsLoading ? (
          <div className="differe-loading"><div className="differe-spinner" /></div>
        ) : tournamentsError ? (
          <div className="differe-error">
            <span>{tournamentsError}</span>
            <button onClick={fetchTournaments}>Réessayer</button>
          </div>
        ) : filteredTournaments.length === 0 ? (
          <div className="differe-empty">Aucun tournoi trouvé</div>
        ) : null}
        {!tournamentsLoading && filteredTournaments.map((t) => {
          const id = t.iddiff;
          const isActive = selectedDiffId === id;
          const typeDisplay = t.type
            ? TYPE_LABELS[t.type] || t.type
            : typeLabel(diffType);
          const subIcon = getSubTypeIcon(t.type, 14);
          const subTypeColor = t.type ? SUB_TYPE_COLORS[t.type] : undefined;
          const hasPlayerRank = t.player_rank != null && t.nb_participants != null;
          const rankQuality = hasPlayerRank ? getRankQuality(t.player_rank!, t.nb_participants!) : null;
          const hasTop = !!t.top_pseudo && !hasPlayerRank;
          const ongoing = isTournamentOngoing(t.fin);
          const topDisplay = t.top_pseudo_display || (t.top_pseudo ? t.top_pseudo.replace(/¶/g, ' ') : '');

          return (
            <button
              key={id}
              className={`differe-tournament-item ${isActive ? 'active' : ''} ${ongoing ? 'ongoing' : ''}`}
              onClick={() => handleTournamentClick(id)}
            >
              {/* Row 1 — type / category, and date range on the right */}
              <div className="differe-tournament-row-top">
                {diffType === 'indiv' ? (
                  <span
                    className="differe-tournament-typechip"
                    style={subTypeColor ? { color: subTypeColor } : undefined}
                  >
                    {subIcon && <span className="differe-type-icon">{subIcon}</span>}
                    <span className="differe-tournament-typetext">{typeDisplay}</span>
                  </span>
                ) : diffType === 'tripl' ? (
                  <span className="differe-tournament-typechip">
                    <TripleteIcon size={14} />
                    <span className="differe-tournament-typetext">Triplette</span>
                  </span>
                ) : (
                  <span className="differe-tournament-typechip">
                    <EquipeIcon size={14} />
                    <span className="differe-tournament-typetext">Équipe</span>
                  </span>
                )}
                <span className="differe-tournament-date">
                  {formatDateRange(t.debut, t.fin)}
                </span>
              </div>

              {/* Row 2 — status / rank / top player */}
              <div className="differe-tournament-row-bottom">
                <span className="differe-tournament-meta">
                  {ongoing && (
                    <span className="differe-ongoing-badge">En cours</span>
                  )}
                  {!ongoing && t.nb_participants != null && (
                    <span className="differe-participants-count">
                      {t.nb_participants}
                      {diffType === 'indiv' ? ' j.' : diffType === 'tripl' ? ' tripl.' : ' éq.'}
                    </span>
                  )}
                </span>

                {/* Right side: either rank info OR top player chip */}
                {hasPlayerRank ? (
                  <span className="differe-tournament-rank-wrap" title={`Classement : ${t.player_rank}/${t.nb_participants}`}>
                    <span className={`differe-rank-badge rank-${rankQuality}`}>
                      #{t.player_rank}
                      <span className="differe-rank-total">/{t.nb_participants}</span>
                    </span>
                    {t.nb_participants! > 1 && (
                      <span className="differe-rank-bar" aria-hidden="true">
                        <span
                          className={`differe-rank-bar-fill rank-${rankQuality}`}
                          style={{ width: `${Math.max(4, ((t.nb_participants! - t.player_rank!) / (t.nb_participants! - 1)) * 100)}%` }}
                        />
                      </span>
                    )}
                  </span>
                ) : hasTop && diffType === 'indiv' ? (
                  <span
                    className="differe-top-avatar-wrap"
                    onClick={(e) => {
                      e.stopPropagation();
                      const rect = (e.currentTarget as HTMLElement).getBoundingClientRect();
                      setTopPopover(prev =>
                        prev?.diffId === id ? null : {
                          diffId: id,
                          rect,
                          pseudos: [t.top_pseudo!],
                          displays: [topDisplay],
                        }
                      );
                    }}
                  >
                    <span className="differe-top-label">Top</span>
                    {(!t.top_count || t.top_count === 1) && (
                      <span className="differe-top-name">{topDisplay}</span>
                    )}
                    {t.top_count && t.top_count > 1 && (
                      <span className="differe-top-name">{topDisplay} <span className="differe-top-plus">+{t.top_count - 1}</span></span>
                    )}
                    <PlayerAvatar pseudo={t.top_pseudo!} size={20} />
                  </span>
                ) : diffType === 'tripl' && t.joueurs && t.joueurs.length > 0 ? (
                  <span className="differe-tripl-compact">
                    {t.joueurs.slice(0, 3).map((pseudo, i) => (
                      <PlayerAvatar key={`${pseudo}-${i}`} pseudo={pseudo} size={18} />
                    ))}
                  </span>
                ) : null}
              </div>
            </button>
          );
        })}
      </div>

      {/* Floating popover for top player(s) */}
      {topPopover && (
        <div
          className="differe-top-popover"
          style={{
            top: topPopover.rect.bottom + 4,
            left: Math.min(topPopover.rect.left, window.innerWidth - 200),
          }}
        >
          <div className="differe-top-popover-title">Premier{topPopover.displays.length > 1 ? 's' : ''} du différé</div>
          {topPopover.displays.map((display, i) => (
            <div key={topPopover.pseudos[i]} className="differe-top-popover-item">
              <PlayerAvatar pseudo={topPopover.pseudos[i]} size={28} />
              <span>{display}</span>
            </div>
          ))}
        </div>
      )}
    </>
  );

  // ==========================================
  // Breadcrumb
  // ==========================================
  const breadcrumb = (
    <div className="differe-breadcrumb">
      <button
        className={`differe-breadcrumb-item ${detailView === 'results' ? 'active' : ''}`}
        onClick={() => { setDetailView('results'); setReplayHistory(null); }}
      >
        Résultats
      </button>
      {(detailView === 'donnes' || detailView === 'replay') && (
        <>
          <span className="differe-breadcrumb-sep">›</span>
          <button
            className={`differe-breadcrumb-item differe-breadcrumb-player ${detailView === 'donnes' ? 'active' : ''}`}
            onClick={() => { setDetailView('donnes'); setReplayHistory(null); }}
          >
            {selectedPlayerPseudo && diffType === 'indiv' && (
              <PlayerAvatar pseudo={selectedPlayerPseudo} size={22} />
            )}
            {selectedPlayerDisplay || 'Donnes'}
          </button>
        </>
      )}
      {detailView === 'replay' && (
        <>
          <span className="differe-breadcrumb-sep">›</span>
          <span className="differe-breadcrumb-item active">
            Donne {replayDonneNum}
          </span>
        </>
      )}
    </div>
  );

  // ==========================================
  // Results table — sorted by user-chosen column
  // ==========================================
  const sortedResults = useMemo(() => {
    if (results.length === 0) return results;
    const sign = sortDir === 'desc' ? -1 : 1;
    const arr = [...results];
    arr.sort((a, b) => {
      const av = Number(a[sortKey]) || 0;
      const bv = Number(b[sortKey]) || 0;
      if (av === bv) return 0;
      return av < bv ? -sign : sign;
    });
    return arr;
  }, [results, sortKey, sortDir]);

  // Pre-rank items by tot (descending) once — that's the official ranking
  const rankByTot = useMemo(() => {
    const map = new Map<number | string, number>();
    const arr = [...results].sort((a, b) => (Number(b.tot) || 0) - (Number(a.tot) || 0));
    arr.forEach((r, idx) => {
      const key = r.pseudo || r.idtriplette || r.idequipe || idx;
      map.set(key, idx + 1);
    });
    return map;
  }, [results]);

  const handleSortClick = useCallback((key: SortKey) => {
    if (sortKey === key) {
      setSortDir(d => (d === 'desc' ? 'asc' : 'desc'));
    } else {
      setSortKey(key);
      setSortDir('desc');
    }
  }, [sortKey]);

  const renderResults = () => {
    if (resultsLoading) {
      return <div className="differe-loading"><div className="differe-spinner" /></div>;
    }
    if (resultsError) {
      return (
        <div className="differe-error">
          <span>{resultsError}</span>
          <button onClick={() => selectedDiffId && fetchResults(selectedDiffId)}>Réessayer</button>
        </div>
      );
    }
    if (results.length === 0) {
      return <div className="differe-empty">Aucun résultat disponible</div>;
    }

    const isIndiv = diffType === 'indiv';
    const isTripl = diffType === 'tripl';
    const myPseudo = session.pseudo.toLowerCase();
    const trackedPseudo = effectivePlayer?.toLowerCase() || null;
    const selectedTournamentData = tournaments.find(t => t.iddiff === selectedDiffId);
    const isOngoing = selectedTournamentData ? isTournamentOngoing(selectedTournamentData.fin) : false;

    // Total label depends on differe type
    const totLabel = !showAttDef && monoCategory === 'att' ? 'Score Att.'
                    : !showAttDef && monoCategory === 'def' ? 'Score Déf.'
                    : 'Total';

    const renderSortArrow = (key: SortKey) => {
      if (sortKey !== key) return null;
      return <span className="differe-sort-arrow">{sortDir === 'desc' ? '▾' : '▴'}</span>;
    };

    return (
      <div className="differe-results-table">
        <div className="differe-results-header">
          <span className="differe-col-rank">#</span>
          <span className="differe-col-player">
            {isIndiv ? 'Joueur' : isTripl ? 'Triplette' : 'Équipe'}
          </span>
          <button
            type="button"
            className={`differe-col-score differe-col-sortable ${sortKey === 'tot' ? 'is-sorted' : ''}`}
            onClick={() => handleSortClick('tot')}
          >
            {totLabel}{renderSortArrow('tot')}
          </button>
          {showAttDef && (
            <>
              <button
                type="button"
                className={`differe-col-score-sm differe-col-att differe-col-sortable ${sortKey === 'att' ? 'is-sorted' : ''}`}
                onClick={() => handleSortClick('att')}
              >
                Att{renderSortArrow('att')}
              </button>
              <button
                type="button"
                className={`differe-col-score-sm differe-col-def differe-col-sortable ${sortKey === 'def' ? 'is-sorted' : ''}`}
                onClick={() => handleSortClick('def')}
              >
                Déf{renderSortArrow('def')}
              </button>
            </>
          )}
        </div>
        <div className="differe-results-body" ref={resultsBodyRef}>
          {sortedResults.map((item, idx) => {
            let displayName: string;
            let pseudoKey: string | number;
            let isMe = false;
            let isTracked = false;
            let playerParam: string;
            const triplMembers: { pseudo: string; display: string }[] = [];

            if (isIndiv) {
              displayName = item.pseudo_display || item.pseudo || '';
              pseudoKey = item.pseudo || '';
              isMe = String(pseudoKey).toLowerCase() === myPseudo;
              if (trackedPseudo) isTracked = String(pseudoKey).toLowerCase() === trackedPseudo;
              playerParam = item.pseudo!;
            } else if (isTripl) {
              const members = [
                { pseudo: item.ouest || '', display: item.ouest_display || item.ouest || '' },
                { pseudo: item.nord || '', display: item.nord_display || item.nord || '' },
                { pseudo: item.est || '', display: item.est_display || item.est || '' },
              ];
              triplMembers.push(...members);
              displayName = members.map(m => m.display).join(' / ');
              pseudoKey = item.idtriplette ?? `t-${idx}`;
              isMe = members.some(m => m.pseudo.toLowerCase() === myPseudo);
              if (trackedPseudo) isTracked = members.some(m => m.pseudo.toLowerCase() === trackedPseudo);
              playerParam = members[0].pseudo;
            } else {
              displayName = item.equipe_nom || `Équipe ${item.idequipe}`;
              pseudoKey = item.idequipe ?? `q-${idx}`;
              playerParam = String(item.idequipe);
            }

            // Official rank (always by tot, regardless of sort)
            const officialRank = rankByTot.get(pseudoKey) ?? idx + 1;

            // Percentage for the sorted column (or tot)
            const pctTot = surTotals.tot > 0 ? (Number(item.tot) || 0) / surTotals.tot * 100 : 0;
            const pctAtt = surTotals.att > 0 ? (Number(item.att) || 0) / surTotals.att * 100 : 0;
            const pctDef = surTotals.def > 0 ? (Number(item.def) || 0) / surTotals.def * 100 : 0;

            return (
              <div
                key={pseudoKey}
                className={`differe-results-row ${isMe ? 'is-me' : ''} ${isTracked ? 'is-tracked' : ''} ${isOngoing ? 'is-ongoing' : ''}`}
                onClick={isOngoing ? undefined : () => handlePlayerClick(playerParam, displayName)}
                title={isOngoing ? 'Différé en cours — détails disponibles à la fin' : undefined}
              >
                <span className="differe-col-rank">
                  {officialRank <= 3
                    ? <span className={`differe-medal rank-${officialRank}`}>{officialRank}</span>
                    : officialRank}
                </span>
                <div className="differe-col-player">
                  {isIndiv && (
                    <>
                      <PlayerAvatar pseudo={item.pseudo || ''} size={28} />
                      <span className="differe-player-name" title={displayName}>
                        {displayName}
                      </span>
                    </>
                  )}
                  {isTripl && (
                    <div className="differe-tripl-cell">
                      {triplMembers.map((m) => (
                        <div key={m.pseudo} className="differe-tripl-member">
                          <PlayerAvatar pseudo={m.pseudo} size={22} />
                          <span>{m.display.replace(/¶/g, ' ')}</span>
                        </div>
                      ))}
                    </div>
                  )}
                  {!isIndiv && !isTripl && (
                    <span className="differe-player-name" title={displayName}>
                      {displayName}
                    </span>
                  )}
                </div>
                <span className="differe-col-score">
                  <PercentCell value={pctTot} title={`${fmtScoreText(item.tot)} / ${surTotals.tot}`} />
                </span>
                {showAttDef && (
                  <>
                    <span className="differe-col-score-sm differe-col-att">
                      <PercentCell value={pctAtt} compact title={`${fmtScoreText(item.att)} / ${surTotals.att}`} />
                    </span>
                    <span className="differe-col-score-sm differe-col-def">
                      <PercentCell value={pctDef} compact title={`${fmtScoreText(item.def)} / ${surTotals.def}`} />
                    </span>
                  </>
                )}
              </div>
            );
          })}
        </div>
      </div>
    );
  };

  // ==========================================
  // By-donne view
  // ==========================================
  const byDonneList = useMemo(() => {
    if (results.length === 0) return [] as Array<{
      idx: number;
      isAtt: boolean;
      effectif: number;
      topScore: number | null;
      tops: Array<{ pseudo?: string; display: string; idtriplette?: number; idequipe?: number; ouest?: string; nord?: string; est?: string }>;
    }>;
    return Array.from({ length: donneCount }, (_, i) => {
      const scores = donnePerScores[i] || [];
      const valid: number[] = scores.filter((s): s is number => s !== null);
      const topScore = valid.length ? Math.max(...valid) : null;
      const tops: Array<{ pseudo?: string; display: string; idtriplette?: number; idequipe?: number; ouest?: string; nord?: string; est?: string }> = [];
      if (topScore !== null) {
        for (let r = 0; r < results.length; r++) {
          if (scores[r] === topScore) {
            const item = results[r];
            if (diffType === 'indiv') {
              tops.push({ pseudo: item.pseudo, display: item.pseudo_display || item.pseudo || '' });
            } else if (diffType === 'tripl') {
              const display = [item.ouest_display || item.ouest, item.nord_display || item.nord, item.est_display || item.est]
                .filter(Boolean).join(' / ').replace(/¶/g, ' ');
              tops.push({
                idtriplette: item.idtriplette,
                ouest: item.ouest, nord: item.nord, est: item.est,
                display,
              });
            } else {
              tops.push({ idequipe: item.idequipe, display: item.equipe_nom || `Équipe ${item.idequipe}` });
            }
          }
        }
      }
      return {
        idx: i,
        isAtt: isAttDonne(i, attDonneSet),
        effectif: effectifPerDonne[i] || 0,
        topScore,
        tops,
      };
    });
  }, [results, donneCount, donnePerScores, effectifPerDonne, attDonneSet, diffType]);

  const renderByDonne = () => {
    if (resultsLoading) {
      return <div className="differe-loading"><div className="differe-spinner" /></div>;
    }
    if (results.length === 0 || donneCount === 0) {
      return <div className="differe-empty">Aucune donne disponible</div>;
    }

    return (
      <div className="differe-bydonne-table">
        <div className="differe-bydonne-header">
          <span className="differe-bydonne-col-num">#</span>
          {showAttDef && <span className="differe-bydonne-col-type">Type</span>}
          <span className="differe-bydonne-col-eff">Effectif</span>
          <span className="differe-bydonne-col-tops">Top{!showAttDef ? ' du diff.' : ''}</span>
        </div>
        <div className="differe-bydonne-body">
          {byDonneList.map((d) => (
            <div key={d.idx} className="differe-bydonne-row">
              <span className="differe-bydonne-col-num">
                <span className="differe-bydonne-numbadge">{d.idx + 1}</span>
              </span>
              {showAttDef && (
                <span className="differe-bydonne-col-type">
                  {d.isAtt ? (
                    <span className="differe-donne-typechip att">
                      <AttackIcon size={12} />Att
                    </span>
                  ) : (
                    <span className="differe-donne-typechip def">
                      <DefenseIcon size={12} />Déf
                    </span>
                  )}
                </span>
              )}
              <span className="differe-bydonne-col-eff">
                {d.effectif > 0 ? d.effectif : <span className="differe-bydonne-muted">—</span>}
              </span>
              <span className="differe-bydonne-col-tops">
                {d.tops.length === 0 ? (
                  <span className="differe-bydonne-muted">non jouée</span>
                ) : (
                  <div className="differe-bydonne-tops">
                    {d.tops.slice(0, 8).map((p, k) => (
                      <span key={k} className="differe-bydonne-top-chip" title={p.display}>
                        {p.pseudo
                          ? <PlayerAvatar pseudo={p.pseudo} size={20} />
                          : p.ouest
                            ? <PlayerAvatar pseudo={p.ouest} size={20} />
                            : null}
                        <span className="differe-bydonne-top-name">{p.display}</span>
                      </span>
                    ))}
                    {d.tops.length > 8 && (
                      <span className="differe-bydonne-top-more">+{d.tops.length - 8}</span>
                    )}
                  </div>
                )}
              </span>
            </div>
          ))}
        </div>
      </div>
    );
  };

  // ==========================================
  // Donnes view
  // ==========================================
  const renderDonnes = () => {
    if (donnesLoading) {
      return <div className="differe-loading"><div className="differe-spinner" /></div>;
    }
    if (donnesError) {
      return (
        <div className="differe-error">
          <span>{donnesError}</span>
        </div>
      );
    }
    if (donnes.length === 0 && !donneScores) {
      return <div className="differe-empty">Aucune donne disponible</div>;
    }

    // Summary row
    const totScore = donneScores?.tot;
    const attScore = donneScores?.att;
    const defScore = donneScores?.def;
    const totRank = effectif > 0 && totScore != null
      ? getRankInList(Number(totScore), results.map(r => r.tot))
      : null;

    return (
      <div className="differe-donnes-view">
        {/* Summary */}
        {donneScores && (
          <div className="differe-donnes-summary">
            <div className="differe-donnes-summary-item">
              <span className="differe-donnes-label">{!showAttDef && monoCategory === 'att' ? 'Attaque' : !showAttDef && monoCategory === 'def' ? 'Défense' : 'Total'}</span>
              <span className="differe-donnes-value">
                {surTotals.tot > 0
                  ? <>{((Number(totScore) || 0) / surTotals.tot * 100).toFixed(1)}<span className="differe-pct-suffix">%</span></>
                  : fmtScore(Number(totScore))}
              </span>
              <span className="differe-donnes-pct">
                {totRank != null && `#${totRank} / ${effectif}`}
              </span>
            </div>
            {showAttDef && (
              <>
                <div className="differe-donnes-summary-item">
                  <span className="differe-donnes-label">Attaque</span>
                  <span className="differe-donnes-value">
                    {surTotals.att > 0
                      ? <>{((Number(attScore) || 0) / surTotals.att * 100).toFixed(1)}<span className="differe-pct-suffix">%</span></>
                      : fmtScore(Number(attScore))}
                  </span>
                </div>
                <div className="differe-donnes-summary-item">
                  <span className="differe-donnes-label">Défense</span>
                  <span className="differe-donnes-value">
                    {surTotals.def > 0
                      ? <>{((Number(defScore) || 0) / surTotals.def * 100).toFixed(1)}<span className="differe-pct-suffix">%</span></>
                      : fmtScore(Number(defScore))}
                  </span>
                </div>
              </>
            )}
          </div>
        )}

        {/* Donne list */}
        <div className="differe-donnes-list">
          <div className="differe-donnes-header">
            <span className="differe-donnes-col-num">Donne</span>
            {showAttDef && <span className="differe-donnes-col-typecol">Type</span>}
            <span className="differe-donnes-col-pct">Score</span>
            <span className="differe-donnes-col-rank">Rang</span>
            <span className="differe-donnes-col-action"></span>
          </div>
          {donnes.map((donne) => {
            const donneIdx = Number(donne.numdonne) - 1;
            const scoreKey = getScoreKey(donneIdx, 's');
            const pointsKey = getScoreKey(donneIdx, 'p');
            const sRaw = donneScores ? donneScores[scoreKey] : undefined;
            const pRaw = donneScores ? donneScores[pointsKey] : undefined;
            const hasScore = sRaw != null && sRaw !== '';
            const score = hasScore ? Number(sRaw) : 0;
            const points = pRaw != null && pRaw !== '' ? Number(pRaw) : 0;
            const eff = effectifPerDonne[donneIdx] || effectif;
            const pct = getPercentage(points, eff);
            const allScoresForDonne = donneAllScores[donneIdx] || [];
            const rank = hasScore ? getRankInList(score, allScoresForDonne) : 0;
            const isAtt = isAttDonne(donneIdx, attDonneSet);

            return (
              <div key={donne.id} className="differe-donnes-row">
                <span className="differe-donnes-col-num">{donne.numdonne}</span>
                {showAttDef && (
                  <span className="differe-donnes-col-typecol">
                    {isAtt ? (
                      <span className="differe-donne-typechip att" title="Donne d'attaque">
                        <AttackIcon size={12} />
                      </span>
                    ) : (
                      <span className="differe-donne-typechip def" title="Donne de défense">
                        <DefenseIcon size={12} />
                      </span>
                    )}
                  </span>
                )}
                <span className="differe-donnes-col-pct">
                  <span className="differe-pct-bar-container">
                    <span
                      className={`differe-pct-bar ${pct >= 60 ? 'good' : pct >= 40 ? 'mid' : 'low'}`}
                      style={{ width: `${Math.min(100, Math.max(0, pct))}%` }}
                    />
                    <span className="differe-pct-text">{hasScore ? `${pct.toFixed(1)}%` : '—'}</span>
                  </span>
                </span>
                <span className="differe-donnes-col-rank">
                  {!hasScore
                    ? <span className="differe-bydonne-muted">—</span>
                    : rank <= 3
                      ? <span className={`differe-medal rank-${rank}`}>{rank}</span>
                      : `${rank}/${eff}`
                  }
                </span>
                <span className="differe-donnes-col-action">
                  <button
                    className="differe-replay-btn"
                    onClick={() => fetchReplay(donne.id, donne.numdonne)}
                    disabled={replayLoading}
                    title="Voir le replay"
                  >
                    ▶
                  </button>
                </span>
              </div>
            );
          })}
        </div>
      </div>
    );
  };

  // ==========================================
  // Open donne on training page
  // ==========================================
  const handleOpenInTraining = useCallback(async () => {
    if (!replayHistory || selectedDiffId === null || donnes.length === 0) return;

    setTrainingExportLoading(true);
    try {
      const tournament = tournaments.find((t) => t.iddiff === selectedDiffId);
      const playerLabel = followedPlayer?.display
        || selectedPlayerDisplay
        || effectivePlayer
        || session.pseudo;
      const batchTitle = resolveDiffereBatchTitle(
        diffType,
        tournament?.type ?? differeInfo?.type,
        playerLabel,
      );

      const batch = await buildDiffereCompareReplayBatch({
        diffType,
        differeId: selectedDiffId,
        playerPseudo: effectivePlayer ?? selectedPlayerPseudo ?? session.pseudo,
        playerLabel,
        typeLabel: batchTitle,
        selectedDealNum: replayDonneNum ?? undefined,
        donnes: donnes.map((d) => ({ id: Number(d.id), numdonne: Number(d.numdonne) })),
      });

      const selectedDeal = batch.deals[batch.selectedIndex] ?? batch.deals[0];

      sessionStorage.setItem('library_load_request', JSON.stringify({
        compareReplayBatch: batch,
        mode: 'replay',
        position: 0,
        replayAllBots: true,
        historyContext: {
          kind: 'differe',
          differe: {
            differeId: selectedDiffId,
            differeType: diffType,
            subType: tournament?.type ?? differeInfo?.type,
            dateStart: tournament?.debut ?? differeInfo?.debut,
            dateEnd: tournament?.fin ?? differeInfo?.fin,
            pseudo: effectivePlayer ?? selectedPlayerPseudo ?? session.pseudo,
            pseudoDisplay: playerLabel,
            dealNum: selectedDeal?.dealIndex ?? replayDonneNum ?? undefined,
            partieId: selectedDeal?.partieId,
          },
        },
      }));

      startTraining({
        sessionId: '',
        displayMode: 'cards_on_table',
        showTimeline: true,
        botConfig: { disabledBots: new Set() },
      });
    } catch (e) {
      console.error('Open in training failed:', e);
    } finally {
      setTrainingExportLoading(false);
    }
  }, [
    replayHistory,
    startTraining,
    selectedDiffId,
    diffType,
    differeInfo,
    tournaments,
    effectivePlayer,
    selectedPlayerPseudo,
    session.pseudo,
    followedPlayer,
    selectedPlayerDisplay,
    replayDonneNum,
    donnes,
  ]);

  // ==========================================
  // Replay view
  // ==========================================
  const renderLibraryMatchPanel = () => {
    if (libraryMatchLoading) {
      return (
        <div className="differe-library-match differe-library-match--loading" aria-live="polite">
          <span className="differe-library-match-label">Bibliothèque…</span>
        </div>
      );
    }
    if (!libraryMatch) return null;

    const addedDate = new Date(libraryMatch.created_at).toLocaleDateString('fr-FR', {
      day: 'numeric',
      month: 'short',
      year: 'numeric',
    });

    return (
      <div className="differe-library-match">
        <div className="differe-library-match-head">
          <span className="differe-library-match-label">📚 Dans votre bibliothèque</span>
          <span className="differe-library-match-date">ajoutée le {addedDate}</span>
        </div>
        {libraryMatch.tags.length > 0 && (
          <div className="differe-library-match-tags">
            {libraryMatch.tags.map((tag) => (
              <span key={tag} className="differe-library-match-tag" data-tag={tag}>{tag}</span>
            ))}
          </div>
        )}
        {libraryMatch.note && (
          <p className="differe-library-match-note">{libraryMatch.note}</p>
        )}
        {libraryMatch.author_comment && (
          <p className="differe-library-match-comment">{libraryMatch.author_comment}</p>
        )}
        {(libraryMatch.comment_count ?? 0) > 0 && !libraryMatch.author_comment && (
          <span className="differe-library-match-meta">
            {libraryMatch.comment_count} commentaire{(libraryMatch.comment_count ?? 0) > 1 ? 's' : ''}
          </span>
        )}
        <button
          type="button"
          className="differe-library-match-link"
          onClick={() => navigateToLibrary({
            section: 'own',
            ownGameId: libraryMatch.id,
            highlightOnly: true,
          })}
        >
          Voir en bibliothèque
        </button>
      </div>
    );
  };

  const renderReplay = () => {
    if (replayLoading) {
      return <div className="differe-loading"><div className="differe-spinner" /></div>;
    }
    if (!replayHistory) {
      return <div className="differe-empty">Impossible de charger le replay</div>;
    }
    const viewedLabel = selectedPlayerDisplay || followedPlayer?.display || replayViewerParam;

    const compareSelector = (
      <select
        className="differe-compare-select"
        value={comparePlayer?.param ?? ''}
        onChange={(e) => {
          const found = compareCandidates.find((c) => c.playerParam === e.target.value);
          setComparePlayer(found ? { param: found.playerParam, display: found.display } : null);
        }}
        title="Choisir le joueur à comparer"
      >
        {compareCandidates.length === 0 && <option value="">Aucun autre joueur</option>}
        {compareCandidates.map((c) => (
          <option key={c.key} value={c.playerParam}>
            {c.display}
            {c.score != null ? ` (${fmtScoreText(c.score)})` : ''}
          </option>
        ))}
      </select>
    );

    const dualToggle = (
      <button
        className={`differe-replay-action-btn ${dualMode ? 'is-active' : ''}`}
        onClick={() => setDualMode((v) => !v)}
        title={dualMode ? 'Revenir à une seule table' : 'Comparer avec un autre joueur'}
      >
        {dualMode ? '⬛ Une table' : '⬛⬛ Comparer'}
      </button>
    );

    return (
      <div className="differe-replay-container">
        {dualMode && dualLeftHistory ? (
          <ReplayDualMode
            left={{ history: dualLeftHistory, label: viewedLabel }}
            right={compareHistory && comparePlayer
              ? { history: compareHistory, label: comparePlayer.display }
              : null}
            rightHeaderExtra={compareSelector}
            rightPlaceholder={
              compareLoading
                ? 'Chargement de la table comparée…'
                : compareError ?? 'Choisissez un joueur à comparer.'
            }
            actions={dualToggle}
          />
        ) : (
          <ReplayMode history={replayHistory} />
        )}
        <div className="differe-replay-actions">
          {!dualMode && dualToggle}
          <button
            className="differe-replay-action-btn"
            onClick={() => setShowShareLinkModal(true)}
            disabled={!currentDiffereShareRequest}
            title="Partager un lien vers cette donne"
          >
            🔗 Partager
          </button>
          <button
            className="differe-replay-action-btn"
            onClick={() => setShowLibraryModal(true)}
            disabled={!!libraryMatch || libraryMatchLoading}
            title={libraryMatch ? 'Déjà dans votre bibliothèque' : 'Ajouter à la bibliothèque'}
          >
            {libraryMatch ? '📚 En bibliothèque' : '📚 Bibliothèque'}
          </button>
          <button
            className="differe-replay-action-btn"
            onClick={() => void handleOpenInTraining()}
            disabled={trainingExportLoading || donnes.length === 0}
            title="Rejouer les donnes du joueur en entraînement"
          >
            {trainingExportLoading ? 'Chargement…' : '🎯 Entraînement'}
          </button>
        </div>
        {showLibraryModal && replayHistory && selectedDiffId !== null && (
          <AddToLibraryModal
            pseudo={session.pseudo}
            history={replayHistory}
            originType={diffType === 'indiv' ? 'differe_indiv' : 'differe_tq'}
            originId={String(selectedDiffId)}
            originDonneNum={replayDonneNum ?? undefined}
            originMetadata={buildLibraryDiffereOriginMetadata({
              diffType,
              differeId: selectedDiffId,
              subType: differeInfo?.type ?? tournaments.find((t) => t.iddiff === selectedDiffId)?.type,
              dateStart: differeInfo?.debut ?? tournaments.find((t) => t.iddiff === selectedDiffId)?.debut,
              dateEnd: differeInfo?.fin ?? tournaments.find((t) => t.iddiff === selectedDiffId)?.fin,
              playerPseudo: selectedPlayerPseudo ?? effectivePlayer ?? session.pseudo,
              playerDisplay: selectedPlayerDisplay || followedPlayer?.display,
              dealNum: replayDonneNum ?? undefined,
              partieId: currentDonneIdx >= 0
                ? Number(playerDonneList[currentDonneIdx]?.donne.id)
                : donnes.find((d) => Number(d.numdonne) === replayDonneNum)?.id,
            })}
            myPosition={
              replayHistory.players.find((p) => {
                const target = (selectedPlayerPseudo ?? effectivePlayer ?? session.pseudo)
                  .toLowerCase()
                  .replace(/¶/g, ' ')
                  .trim();
                return p.name.toLowerCase().replace(/¶/g, ' ').trim() === target;
              })?.position ?? 0
            }
            onClose={() => setShowLibraryModal(false)}
            onSaved={() => { void refreshLibraryMatch(); }}
          />
        )}
        {showShareLinkModal && currentDiffereShareRequest && (
          <DiffereShareLinkModal
            request={currentDiffereShareRequest}
            playerLabel={selectedPlayerDisplay || followedPlayer?.display || currentDiffereShareRequest.playerPseudo}
            dealLabel={replayDonneNum != null ? String(replayDonneNum) : undefined}
            onClose={() => setShowShareLinkModal(false)}
          />
        )}
      </div>
    );
  };

  // ==========================================
  // Replay navigation (prev/next + donne picker)
  // ==========================================
  const navigateToDonne = useCallback((donne: DonneItem) => {
    setShowDonnePicker(false);
    fetchReplay(Number(donne.id), Number(donne.numdonne));
  }, [fetchReplay]);

  const goPrevDonne = useCallback(() => {
    if (currentDonneIdx <= 0) return;
    const prev = playerDonneList[currentDonneIdx - 1];
    if (prev) navigateToDonne(prev.donne);
  }, [currentDonneIdx, playerDonneList, navigateToDonne]);

  const goNextDonne = useCallback(() => {
    if (currentDonneIdx < 0 || currentDonneIdx >= playerDonneList.length - 1) return;
    const next = playerDonneList[currentDonneIdx + 1];
    if (next) navigateToDonne(next.donne);
  }, [currentDonneIdx, playerDonneList, navigateToDonne]);

  const renderReplayNav = () => {
    const hasPrev = currentDonneIdx > 0;
    const hasNext = currentDonneIdx >= 0 && currentDonneIdx < playerDonneList.length - 1;

    return (
      <div className="differe-replay-nav">
        <button
          className="differe-breadcrumb-item"
          onClick={() => { setDetailView('results'); setReplayHistory(null); }}
          title="Retour aux résultats"
        >
          Résultats
        </button>
        <span className="differe-breadcrumb-sep">›</span>
        <button
          className="differe-breadcrumb-item differe-breadcrumb-player"
          onClick={() => { setDetailView('donnes'); setReplayHistory(null); }}
          title="Voir toutes les donnes du joueur"
        >
          {selectedPlayerPseudo && diffType === 'indiv' && (
            <PlayerAvatar pseudo={selectedPlayerPseudo} size={20} />
          )}
          {selectedPlayerDisplay || 'Donnes'}
        </button>
        <span className="differe-breadcrumb-sep">›</span>
        <div className="differe-replay-nav-group">
          <button
            type="button"
            className="differe-replay-nav-arrow"
            disabled={!hasPrev}
            onClick={goPrevDonne}
            title={hasPrev ? `Donne précédente (${playerDonneList[currentDonneIdx - 1]?.donne.numdonne})` : 'Première donne'}
            aria-label="Donne précédente"
          >
            ‹
          </button>
          <button
            type="button"
            className="differe-breadcrumb-item active differe-donne-picker-trigger"
            onClick={(e) => {
              e.stopPropagation();
              setShowDonnePicker(v => !v);
            }}
            title="Choisir une donne"
          >
            Donne {replayDonneNum}
            <span className="differe-donne-picker-caret">▾</span>
          </button>
          <button
            type="button"
            className="differe-replay-nav-arrow"
            disabled={!hasNext}
            onClick={goNextDonne}
            title={hasNext ? `Donne suivante (${playerDonneList[currentDonneIdx + 1]?.donne.numdonne})` : 'Dernière donne'}
            aria-label="Donne suivante"
          >
            ›
          </button>

          {showDonnePicker && (
            <div className="differe-donne-picker" ref={donnePickerRef}>
              <div className="differe-donne-picker-header">
                Donnes de {selectedPlayerDisplay}
              </div>
              <div className="differe-donne-picker-list">
                {playerDonneList.map((d) => {
                  const numdonne = Number(d.donne.numdonne);
                  const isCurrent = numdonne === replayDonneNum;
                  const eff = effectifPerDonne[d.idx] || effectif;
                  const tone = d.pct >= 60 ? 'good' : d.pct >= 40 ? 'mid' : 'low';
                  return (
                    <button
                      key={d.donne.id}
                      type="button"
                      className={`differe-donne-picker-item ${isCurrent ? 'is-current' : ''}`}
                      onClick={() => navigateToDonne(d.donne)}
                    >
                      <span className="differe-donne-picker-num">{numdonne}</span>
                      {showAttDef && (
                        <span className={`differe-donne-typechip ${d.isAtt ? 'att' : 'def'}`}>
                          {d.isAtt ? <AttackIcon size={11} /> : <DefenseIcon size={11} />}
                        </span>
                      )}
                      <span className={`differe-donne-picker-pct tone-${tone}`}>
                        {d.score != null ? `${d.pct.toFixed(1)}%` : '—'}
                      </span>
                      <span className="differe-donne-picker-rank">
                        {d.score != null ? `#${d.rank}/${eff}` : '—'}
                      </span>
                      <span className="differe-donne-picker-score">
                        {d.score != null ? fmtScoreText(d.score) : ''}
                      </span>
                    </button>
                  );
                })}
              </div>
            </div>
          )}
        </div>
      </div>
    );
  };

  // ==========================================
  // Detail panel content
  // ==========================================
  const detailContent = (
    <div className="differe-detail">
      {selectedDiffId === null ? (
        <div className="differe-empty-state">
          <span className="differe-empty-icon">📋</span>
          <span className="differe-empty-text">
            Sélectionnez un tournoi pour voir les résultats
          </span>
        </div>
      ) : (
        <>
          {/* Header with tournament info */}
          <div className="differe-detail-header">
            <div className="differe-detail-header-row">
              <div className="differe-detail-title">
                <span className="differe-detail-title-main">
                  {getCategoryIcon(diffType, 18)}
                  Différé {typeLabel(diffType)}
                  {differeInfo?.type && (
                    <span
                      className="differe-detail-subtype"
                      style={SUB_TYPE_COLORS[differeInfo.type] ? { color: SUB_TYPE_COLORS[differeInfo.type] } : undefined}
                    >
                      {getSubTypeIcon(differeInfo.type, 16)}
                      {TYPE_LABELS[differeInfo.type] || differeInfo.type}
                    </span>
                  )}
                </span>
                {differeInfo && (
                  <span className="differe-detail-date">
                    {formatDateRange(differeInfo.debut, differeInfo.fin)}
                    {results.length > 0 && ` · ${results.length} participants`}
                  </span>
                )}
              </div>

              {/* View-mode toggle (only when looking at the global results) */}
              {detailView === 'results' && results.length > 0 && (
                <div className="differe-viewmode-toggle" role="tablist">
                  <button
                    type="button"
                    role="tab"
                    aria-selected={viewMode === 'byPlayer'}
                    className={`differe-viewmode-btn ${viewMode === 'byPlayer' ? 'active' : ''}`}
                    onClick={() => setViewMode('byPlayer')}
                    title="Classement par joueur"
                  >
                    <span className="differe-viewmode-icon">👥</span>
                    <span className="differe-viewmode-label">Joueurs</span>
                  </button>
                  <button
                    type="button"
                    role="tab"
                    aria-selected={viewMode === 'byDonne'}
                    className={`differe-viewmode-btn ${viewMode === 'byDonne' ? 'active' : ''}`}
                    onClick={() => setViewMode('byDonne')}
                    title="Tops par donne"
                  >
                    <span className="differe-viewmode-icon">🃏</span>
                    <span className="differe-viewmode-label">Donnes</span>
                  </button>
                </div>
              )}

              {detailView === 'replay' && renderLibraryMatchPanel()}
            </div>

            {/* Breadcrumb / replay nav */}
            {detailView === 'replay'
              ? renderReplayNav()
              : breadcrumb}
          </div>

          {/* Content based on view */}
          <div className="differe-detail-content">
            {detailView === 'results' && (viewMode === 'byPlayer' ? renderResults() : renderByDonne())}
            {detailView === 'donnes' && renderDonnes()}
            {detailView === 'replay' && renderReplay()}
          </div>
        </>
      )}
    </div>
  );

  // ==========================================
  // Back title for compact mode
  // ==========================================
  const selectedTournament = tournaments.find(t => t.iddiff === selectedDiffId);
  const detailTitle = selectedDiffId !== null ? (
    <div className="differe-back-title">
      <span>
        {selectedTournament
          ? `${formatDate(selectedTournament.fin)} — ${selectedTournament.type ? (TYPE_LABELS[selectedTournament.type] || selectedTournament.type) : typeLabel(diffType)}`
          : `Différé ${typeLabel(diffType)}`}
      </span>
      {detailView === 'donnes' && (
        <span className="differe-back-subtitle">{selectedPlayerDisplay}</span>
      )}
      {detailView === 'replay' && (
        <span className="differe-back-subtitle">Donne {replayDonneNum}</span>
      )}
    </div>
  ) : undefined;

  return (
    <div className="differe-container">
      <MasterDetail
        master={sidebarContent}
        detail={detailContent}
        detailTitle={detailTitle}
        hasSelection={showMobileDetail}
        onBack={() => {
          if (detailView === 'replay') {
            setDetailView('donnes');
            setReplayHistory(null);
          } else if (detailView === 'donnes') {
            setDetailView('results');
          } else {
            setShowMobileDetail(false);
          }
        }}
      />
    </div>
  );
}

// ============================================
// Formatters (local)
// ============================================

function fmtScore(v: number | undefined | null): React.ReactNode {
  if (v === null || v === undefined) return '-';
  const n = Number(v);
  if (isNaN(n)) return '-';
  const fixed = n.toFixed(1);
  const [int, dec] = fixed.split('.');
  return <>{int}<span className="differe-decimal">,{dec}</span></>;
}

function fmtScoreText(v: number | undefined | null): string {
  if (v === null || v === undefined) return '-';
  const n = Number(v);
  if (isNaN(n)) return '-';
  return n.toFixed(1).replace('.', ',');
}

function PercentCell({
  value,
  compact = false,
  title,
}: { value: number; compact?: boolean; title?: string }) {
  if (!isFinite(value) || value <= 0) {
    return <span className="differe-pct-empty" title={title}>—</span>;
  }
  const fixed = value.toFixed(1);
  const [int, dec] = fixed.split('.');
  const tone = value >= 60 ? 'good' : value >= 40 ? 'mid' : 'low';
  return (
    <span className={`differe-pct-cell ${compact ? 'compact' : ''} tone-${tone}`} title={title}>
      <span className="differe-pct-bar-bg">
        <span
          className="differe-pct-bar-fill"
          style={{ width: `${Math.min(100, Math.max(0, value))}%` }}
        />
      </span>
      <span className="differe-pct-cell-text">
        {int}<span className="differe-decimal">,{dec}</span>
        <span className="differe-pct-suffix">%</span>
      </span>
    </span>
  );
}
